/**
 * Unified auto-services repository. Hides the data source per category so
 * the UI just asks for `list(category, ctx)` and gets back a normalised
 * array sorted by distance (where lat/lon is available).
 *
 * Sources:
 *   - 'gas' / 'garage' / 'inspection' → data.gov.il (govServices.js)
 *   - 'parking' / 'car_wash' / 'car_parts' → OSM (osm.js)
 *
 * @typedef {'gas'|'garage'|'inspection'|'parking'|'car_wash'|'car_parts'} ServiceCategory
 *
 * @typedef {Object} ServiceItem
 * @property {string} id
 * @property {ServiceCategory} kind
 * @property {string} name
 * @property {string} [brand]
 * @property {string} [address]
 * @property {string} [city]
 * @property {string} [phone]
 * @property {string} [profession]
 * @property {string} [openingHours]
 * @property {string} [extra]
 * @property {number} [lat]
 * @property {number} [lon]
 * @property {number} [distanceKm]
 * @property {'gov'|'osm'} source
 */

import * as gov from '../apis/govServices.js';
import * as osm from '../apis/osm.js';
import { haversineKm } from '../../shared/geo.js';
import { createLogger } from '../../core/logger.js';

const log = createLogger('services.repo');

/** Cache TTL — gov data updates rarely, OSM data is volunteer-curated. */
const CACHE_TTL_MS = 6 * 60 * 60 * 1000;     // 6 hours
/**
 * Bump the prefix when fetcher logic changes in a way that could make older
 * cached results stale/wrong. Old entries under previous prefixes are simply
 * ignored — `localStorage` doesn't fill up because the bumped prefix is also
 * used in `clearCache()` below.
 *
 * - v1: initial release. Could cache empty `[]` after a transient failure,
 *       stuck for 6h. Garage fetch used strict yishuv filter.
 * - v2: empty results no longer cached; garage city uses CKAN `q`
 *       (full-text); OSM endpoints raced in parallel.
 * - v3: OSM POIs without names now get reverse-geocoded via LocationIQ to
 *       pick up a usable street address. v2-cached items would be missing
 *       this so they're invalidated.
 */
const CACHE_KEY_PREFIX = 'autome_services_v3_';

/**
 * @param {ServiceCategory} category
 * @param {{ lat?: number; lon?: number; city?: string; radiusM?: number }} ctx
 * @returns {Promise<ServiceItem[]>}
 */
export async function list(category, ctx = {}) {
  const { lat, lon, city, radiusM } = ctx;

  const cached = readCache(category, ctx);
  if (cached) {
    log.info(`cache hit: ${category} (${cached.length} items)`);
    return decorate(cached, lat, lon);
  }

  /** @type {ServiceItem[]} */
  let items = [];
  try {
    items = await fetchByCategory(category, { lat, lon, city, radiusM });
  } catch (err) {
    log.warn(`fetch failed for ${category}:`, err);
    return [];
  }

  // Only cache non-empty results. An empty array is almost always a transient
  // failure (Overpass timeout, CKAN proxy hiccup) — caching it for 6 hours
  // would leave the user stuck on "no results" even after the upstream
  // recovers.
  if (items.length > 0) writeCache(category, ctx, items);
  return decorate(items, lat, lon);
}

/** Clear all cached service lists (every cache version, not just current).
 *  Exposed on `window` so users can run `__services.clearCache()` from
 *  devtools to force a fresh fetch. */
export function clearCache() {
  try {
    const keys = Object.keys(localStorage);
    for (const k of keys) {
      if (k.startsWith('autome_services_')) localStorage.removeItem(k);
    }
  } catch { /* localStorage may be disabled */ }
}

// Expose under a debug global so the user can wipe stale caches from
// devtools without rebuilding: `window.__services.clearCache()`.
if (typeof window !== 'undefined') {
  /** @type {any} */ (window).__services = { clearCache };
}

// ====================================================================
// internals
// ====================================================================

/**
 * @param {ServiceCategory} category
 * @param {{ lat?: number; lon?: number; city?: string; radiusM?: number }} ctx
 * @returns {Promise<ServiceItem[]>}
 */
async function fetchByCategory(category, ctx) {
  switch (category) {
    case 'gas': {
      const rows = await gov.fetchGasStations();
      return rows.map((r) => ({ ...r, source: /** @type {const} */ ('gov') }));
    }
    case 'garage': {
      const rows = await gov.fetchGarages({ city: ctx.city });
      return rows.map((r) => ({ ...r, source: /** @type {const} */ ('gov') }));
    }
    case 'inspection': {
      const rows = await gov.fetchInspectionCenters({ city: ctx.city });
      return rows.map((r) => ({ ...r, source: /** @type {const} */ ('gov') }));
    }
    case 'parking':
    case 'car_wash':
    case 'car_parts': {
      if (!Number.isFinite(ctx.lat) || !Number.isFinite(ctx.lon)) return [];
      const rows = await osm.fetchNearby({
        kind: category,
        lat: /** @type {number} */ (ctx.lat),
        lon: /** @type {number} */ (ctx.lon),
        radiusM: ctx.radiusM,
      });
      return rows.map((r) => ({ ...r, source: /** @type {const} */ ('osm') }));
    }
    default:
      log.warn('unknown category:', category);
      return [];
  }
}

/**
 * Attach `distanceKm` (when coords are present + user location is known) and
 * sort by distance ascending.
 *
 * @param {ServiceItem[]} items
 * @param {number|undefined} userLat
 * @param {number|undefined} userLon
 * @returns {ServiceItem[]}
 */
function decorate(items, userLat, userLon) {
  if (!Number.isFinite(userLat) || !Number.isFinite(userLon)) {
    // Sort alphabetically by name when we can't measure distance.
    return [...items].sort((a, b) => a.name.localeCompare(b.name, 'he'));
  }
  /** @type {ServiceItem[]} */
  const decorated = items.map((it) => {
    if (Number.isFinite(it.lat) && Number.isFinite(it.lon)) {
      return { ...it, distanceKm: haversineKm(/** @type {number} */ (userLat), /** @type {number} */ (userLon), /** @type {number} */ (it.lat), /** @type {number} */ (it.lon)) };
    }
    return it;
  });
  return decorated.sort((a, b) => {
    const da = a.distanceKm ?? Infinity;
    const db = b.distanceKm ?? Infinity;
    return da - db;
  });
}

/**
 * Build a cache key that varies with the per-category context that actually
 * affects results (city for gov categories, lat/lon grid for OSM).
 *
 * @param {ServiceCategory} category
 * @param {{ lat?: number; lon?: number; city?: string; radiusM?: number }} ctx
 */
function cacheKey(category, ctx) {
  switch (category) {
    case 'gas':
      return CACHE_KEY_PREFIX + 'gas';
    case 'garage':
    case 'inspection':
      return CACHE_KEY_PREFIX + category + ':' + (ctx.city || 'all');
    case 'parking':
    case 'car_wash':
    case 'car_parts': {
      // Grid the location to ~1 km so nearby queries share cache.
      const gx = Number.isFinite(ctx.lat) ? /** @type {number} */ (ctx.lat).toFixed(2) : '?';
      const gy = Number.isFinite(ctx.lon) ? /** @type {number} */ (ctx.lon).toFixed(2) : '?';
      const r = ctx.radiusM ?? 'def';
      return CACHE_KEY_PREFIX + category + ':' + gx + ',' + gy + ':' + r;
    }
    default:
      return CACHE_KEY_PREFIX + 'unknown';
  }
}

/**
 * @param {ServiceCategory} category
 * @param {{ lat?: number; lon?: number; city?: string; radiusM?: number }} ctx
 * @returns {ServiceItem[] | null}
 */
function readCache(category, ctx) {
  try {
    const raw = localStorage.getItem(cacheKey(category, ctx));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed.at !== 'number' || !Array.isArray(parsed.items)) return null;
    if (Date.now() - parsed.at > CACHE_TTL_MS) return null;
    return parsed.items;
  } catch { return null; }
}

/**
 * @param {ServiceCategory} category
 * @param {{ lat?: number; lon?: number; city?: string; radiusM?: number }} ctx
 * @param {ServiceItem[]} items
 */
function writeCache(category, ctx, items) {
  try {
    localStorage.setItem(
      cacheKey(category, ctx),
      JSON.stringify({ at: Date.now(), items }),
    );
  } catch { /* quota or disabled */ }
}
